import React from 'react';
import {
  CardColor,
  CARD_COLORS,
  ChatQuestion,
  ChatQuestionOption,
  CARD_BORDER_COLORS,
} from '../config';
import WebAudioPlayer from '../../WebStoryScreen/Components/WebAudioPlayer';
import WebShadowButton from '../../WebStoryScreen/Components/WebShadowButton';
import closeIcon from '../../../appAssets/closeIcon.png';
import {stripSignedUrlQuery} from '../../../Utilities/utilFunctions';

interface UserQuestionModalProps {
  questions: ChatQuestion[];
  cardColor: CardColor;
  onCardColorChange: (color: CardColor) => void;
  onAskTroo: (answer: ChatQuestionOption) => void;
  onClose: () => void;
}

const UserQuestionModal: React.FC<UserQuestionModalProps> = ({
  questions,
  cardColor,
  onCardColorChange,
  onAskTroo,
  onClose,
}) => {
  const isLarge = window.innerWidth >= 768;

  if (!questions || questions.length === 0) return null;

  const currentIndex = CARD_COLORS.indexOf(cardColor);
  const currentQuestion = questions[currentIndex] ?? null;
  const answer = currentQuestion?.options[0] ?? null;
  const borderColor = CARD_BORDER_COLORS[cardColor];

  const modalWidth = Math.min(window.innerWidth * 0.92, isLarge ? 720 : 580);
  const modalHeight = Math.min(window.innerHeight * 0.88, isLarge ? 600 : 520);

  const handlePrev = () => {
    if (currentIndex > 0) {
      onCardColorChange(CARD_COLORS[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (currentIndex < CARD_COLORS.length - 1) {
      onCardColorChange(CARD_COLORS[currentIndex + 1]);
    }
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const containerStyle: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 20,
    border: `3px solid ${borderColor}`,
    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
    width: modalWidth,
    height: modalHeight,
    display: 'flex',
    flexDirection: 'column',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  };

  const dotsRowStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    alignItems: 'center',
  };

  const slideAreaStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  };

  const questionAreaStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: 12,
    paddingRight: 12,
    gap: 12,
  };

  const buttonRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 16,
  };

  return (
    <div style={overlayStyle}>
      <div style={containerStyle}>
        <div style={headerStyle}>
          <div style={dotsRowStyle}>
            {CARD_COLORS.map(color => {
              const isActive = color === cardColor;
              const dotStyle: React.CSSProperties = {
                width: isActive ? 52 : 28,
                height: 28,
                borderRadius: 14,
                backgroundColor: CARD_BORDER_COLORS[color],
                opacity: isActive ? 1 : 0.6,
                cursor: 'pointer',
                border: 'none',
                transition: 'width 0.2s ease',
              };
              return (
                <button
                  key={color}
                  style={dotStyle}
                  onClick={() => onCardColorChange(color)}
                />
              );
            })}
          </div>
          <button
            style={{background: 'none', border: 'none', cursor: 'pointer', padding: 4}}
            onClick={onClose}>
            <img src={closeIcon} alt="close" style={{width: isLarge ? 28 : 22, height: isLarge ? 28 : 22}} />
          </button>
        </div>

        <div style={slideAreaStyle}>
          <button
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              backgroundColor: '#F0F0F0',
              border: 'none',
              cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
              opacity: currentIndex === 0 ? 0.3 : 1,
              fontSize: 26,
              color: currentIndex === 0 ? '#bbb' : '#555',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            onClick={handlePrev}
            disabled={currentIndex === 0}>
            ‹
          </button>

          <div style={questionAreaStyle}>
            <p style={{
              fontSize: isLarge ? 32 : 24,
              color: '#4E4646',
              fontFamily: 'DynaPuff',
              fontWeight: 500,
              textAlign: 'center',
              lineHeight: isLarge ? '29px' : '24px',
              margin: 0,
            }}>
              {currentQuestion?.question ?? 'Loading...'}
            </p>
          </div>

          <button
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              backgroundColor: '#F0F0F0',
              border: 'none',
              cursor: currentIndex === CARD_COLORS.length - 1 ? 'not-allowed' : 'pointer',
              opacity: currentIndex === CARD_COLORS.length - 1 ? 0.3 : 1,
              fontSize: 26,
              color: currentIndex === CARD_COLORS.length - 1 ? '#bbb' : '#555',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            onClick={handleNext}
            disabled={currentIndex === CARD_COLORS.length - 1}>
            ›
          </button>
        </div>

        <div style={buttonRowStyle}>
          <WebShadowButton
            title="Ask Troo"
            variant="text"
            disabled={!answer}
            onPress={() => answer && onAskTroo(answer)}
          />
        </div>

        {currentQuestion?.audioUrl ? (
          <WebAudioPlayer key={stripSignedUrlQuery(currentQuestion.audioUrl)} uri={currentQuestion.audioUrl} />
        ) : null}
      </div>
    </div>
  );
};

export default UserQuestionModal;
